import { computed, storeToRefs } from '#imports';
import { useAlertsStore } from '@/stores/alerts/useAlertsStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { ref } from 'vue';
import type { Target, WatchListGetQueryParams } from '~/service/api/types';

export enum AsyncState {
    IDLE,
    IS_LOADING,
    IS_SUCCESS,
    IS_ERROR
}

export default function useWatchList(initialParams: WatchListGetQueryParams = {}, isCards: boolean = false) {
    const userStore = useUserStore();
    const loadingState = ref<AsyncState>(AsyncState.IDLE);

    const { user } = storeToRefs(userStore);

    const key = computed(() =>
        `${QueryEvmKey.WATCH_LISTS}-${JSON.stringify(watchListParams.value)}-${isCards ? 'with-cards' : 'raw'}-${user.value}`
    );

    const watchListParams = ref<WatchListGetQueryParams>(initialParams);

    const { watchList: watchListApi, QueryEvmKey } = useEVMApi();
    const {
        collection
    } = useWTFApi();

    const alertStore = useAlertsStore();

    const changeWatchListParams = (params: WatchListGetQueryParams) => {
        watchListParams.value = { ...watchListParams.value, ...params };
    };

    const showUserUnauthorized = () => {
        alertStore.add({
            title: 'Oops, something went wrong',
            description: 'Please login to your wallet to use the watchlist feature',
            critical: false,
            status: 'type.info'
        });
    };

    const {
        data: watchList,
        status: watchListStatus,
        refresh
    } = useAsyncData(key, async () => {
        if (user.value) {
            const { data } = await watchListApi.watchListsGet(watchListParams.value);

            if (isCards) {
                const uuids = data.map(item => item.uuid);
                const { data: cards } = await collection.collectionPack(uuids);

                return {
                    data,
                    cards
                };
            }
            return {
                data,
                cards: [] as AppWTFCollection[]
            };
        }
        return {
            data: [] as Target[],
            cards: [] as AppWTFCollection[]
        };
    }, {
        default: () => ({
            data: [] as Target[],
            cards: [] as AppWTFCollection[]
        }),
        watch: [user, watchListParams]
    });

    const addToWatchList = async (target: Target) => {
        if (user.value) {
            loadingState.value = AsyncState.IS_LOADING;
            try {
                await watchListApi.addToWatchList(target);
                loadingState.value = AsyncState.IS_SUCCESS;
            }
            catch (err) {
                loadingState.value = AsyncState.IS_ERROR;
                alertStore.add({
                    title: 'Oops, something went wrong',
                    description: 'Error creating watch list',
                    critical: false,
                    status: 'type.error'
                });
            }
            finally {
                setTimeout(
                    () => {
                        loadingState.value = AsyncState.IDLE;
                    },
                    1000
                );
            }
        }
        else {
            showUserUnauthorized();
        }
    };

    const removeFromWatchList = async (uuid: string) => {
        if (user.value && watchList.value) {
            loadingState.value = AsyncState.IS_LOADING;
            try {
                await watchListApi.removeFromWatchList(uuid);
                loadingState.value = AsyncState.IS_SUCCESS;
            }
            catch (e) {
                loadingState.value = AsyncState.IS_ERROR;
                alertStore.add({
                    title: 'Oops, something went wrong',
                    description: 'Error removing from watch list',
                    critical: false,
                    status: 'type.error'
                });
            }
            finally {
                setTimeout(
                    () => {
                        loadingState.value = AsyncState.IDLE;
                    },
                    1000
                );
            }
        }
        else {
            showUserUnauthorized();
        }
    };

    const handleWatchList = async (target: Target) => {
        if (loadingState.value !== AsyncState.IS_LOADING) {
            try {
                if (watchList.value.data.some(item => item.uuid === target.uuid)) {
                    await removeFromWatchList(target.uuid);
                }
                else {
                    await addToWatchList(target);
                }
                await refresh();
            }
            catch (e) {
                console.log(e, 'error change watch list');
            }
        }
    };

    const watchListLoading = computed(() => watchListStatus.value === 'pending' || loadingState.value === AsyncState.IS_LOADING);
    const isError = computed(() => watchListStatus.value === 'error' || loadingState.value === AsyncState.IS_ERROR);
    const isSuccess = computed(() => watchListStatus.value === 'success' || loadingState.value === AsyncState.IS_ERROR);

    return {
        handleWatchList,
        watchList,
        key,
        watchListParams,
        changeWatchListParams,
        isLoading: watchListLoading,
        showUserUnauthorized,
        isSuccess,
        isError
    };
}
